import { useState, useEffect } from 'react'
import { useAdmin } from '../context/AdminContext'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const Dashboard = () => {
  const { siteMode } = useAdmin()
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    totalRevenue: 0,
    totalUsers: 0,
    todayVisitors: 0,
    conversionRate: 0
  })

  const [recentActivity, setRecentActivity] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('adminToken')
      
      if (!token) {
        setError('Please login to view dashboard')
        return
      }

      // Fetch dashboard statistics
      const statsResponse = await fetch(`${API_BASE_URL}/api/admin/dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (statsResponse.ok) {
        const statsData = await statsResponse.json()
        setStats(statsData)
      } else {
        console.error('Failed to fetch stats')
      }

      // Fetch recent activity
      const activityResponse = await fetch(`${API_BASE_URL}/api/admin/dashboard/recent-activity`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (activityResponse.ok) {
        const activityData = await activityResponse.json()
        setRecentActivity(activityData)
      } else {
        console.error('Failed to fetch recent activity')
      }

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount)
  }

  const statsCards = [
    {
      icon: '📦',
      title: 'Total Products',
      value: stats.totalProducts,
      color: 'var(--gradient-primary)'
    },
    {
      icon: '🛍️',
      title: 'Total Orders',
      value: stats.totalOrders,
      color: 'var(--gradient-success)'
    },
    {
      icon: '💰',
      title: 'Total Revenue',
      value: formatCurrency(stats.totalRevenue),
      color: 'var(--gradient-secondary)'
    },
    {
      icon: '👥',
      title: 'Total Users',
      value: stats.totalUsers,
      color: 'var(--gradient-primary)'
    },
    {
      icon: '📊',
      title: "Today's Visitors",
      value: stats.todayVisitors,
      color: 'var(--gradient-success)'
    },
    {
      icon: '📈',
      title: 'Conversion Rate',
      value: `${stats.conversionRate}%`,
      color: 'var(--gradient-secondary)'
    }
  ]

  return (
    <div className="dashboard fade-in">
      {loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div style={{ fontSize: '3rem' }}>⏳</div>
          <p style={{ color: 'var(--text-secondary)' }}>Loading dashboard data...</p>
        </div>
      ) : error ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#ff006e',
          background: 'var(--bg-card)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 0, 110, 0.3)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
          <p>{error}</p>
          <button 
            onClick={fetchDashboardData}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #00ff88, #00d4ff)',
              color: '#000',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            🔄 Retry
          </button>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '2rem' }}>
            <h1 style={{ 
              color: 'var(--text-primary)', 
              fontSize: '2rem', 
              fontWeight: '800',
              marginBottom: '0.5rem' 
            }}>
              Welcome back! 👋
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem' }}>
              Here's what's happening at SSP Biotech today.
            </p>
          </div>

      {/* Site Mode Status */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
        borderRadius: '16px',
        padding: '1.5rem',
        marginBottom: '2rem',
        textAlign: 'center'
      }}>
        <h3 style={{ color: 'var(--text-primary)', marginBottom: '1rem' }}>
          🌐 Current Site Mode
        </h3>
        <div className={`site-mode-badge ${siteMode}`} style={{ 
          display: 'inline-block',
          fontSize: '1.1rem',
          padding: '0.75rem 2rem'
        }}>
          {siteMode === 'portfolio' ? '📋 Portfolio Mode Active' : '🛒 E-commerce Mode Active'}
        </div>
        <p style={{ 
          color: 'var(--text-secondary)', 
          marginTop: '1rem',
          fontSize: '0.9rem' 
        }}>
          {siteMode === 'portfolio' 
            ? 'Visitors see product showcase without purchase options'
            : 'Full shopping experience with cart and checkout enabled'
          }
        </p>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-grid">
        {statsCards.map((card, index) => (
          <div key={index} className="stat-card">
            <div className="stat-icon" style={{ background: card.color }}>
              {card.icon}
            </div>
            <div className="stat-info">
              <h3>{card.value}</h3>
              <p>{card.title}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
        borderRadius: '16px',
        padding: '2rem'
      }}>
        <h2 style={{ 
          color: 'var(--text-primary)', 
          fontSize: '1.5rem', 
          fontWeight: '700',
          marginBottom: '1.5rem'
        }}>
          🕒 Recent Activity
        </h2>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {recentActivity.map(activity => (
            <div key={activity.id} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
              background: 'var(--bg-secondary)',
              borderRadius: '12px',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{
                fontSize: '1.5rem',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--bg-card)',
                borderRadius: '8px'
              }}>
                {activity.icon}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ 
                  color: 'var(--text-primary)', 
                  fontWeight: '500',
                  marginBottom: '0.25rem'
                }}>
                  {activity.message}
                </p>
                <span style={{ 
                  color: 'var(--text-tertiary)', 
                  fontSize: '0.8rem' 
                }}>
                  {activity.time}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
        borderRadius: '16px',
        padding: '2rem',
        marginTop: '2rem'
      }}>
        <h2 style={{ 
          color: 'var(--text-primary)', 
          fontSize: '1.5rem', 
          fontWeight: '700',
          marginBottom: '1.5rem'
        }}>
          ⚡ Quick Actions
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          <button className="btn btn-primary">
            ➕ Add Product
          </button>
          <button className="btn btn-secondary">
            📋 View Orders
          </button>
          <button className="btn btn-success">
            📊 View Analytics
          </button>
          <button className="btn btn-outline">
            ⚙️ Settings
          </button>
        </div>
      </div>
        </>
      )}
    </div>
  )
}

export default Dashboard
